import React, { Profiler } from 'react';

type ReactProfilerPhase = 'mount' | 'update' | 'nested-update';

export interface ReactProfilerSample {
  id: string;
  phase: ReactProfilerPhase;
  actualDuration: number;
  baseDuration: number;
  startTime: number;
  commitTime: number;
  at: number;
  path: string;
}

interface ReactProfilerStore {
  samples: ReactProfilerSample[];
  reset: () => void;
}

declare global {
  interface Window {
    __flashReactProfile?: ReactProfilerStore;
  }
}

const ENABLE_REACT_PROFILER = import.meta.env.VITE_REACT_PROFILER === '1';
const MAX_SAMPLES = Number(import.meta.env.VITE_REACT_PROFILER_MAX_SAMPLES || 2000);

const getStore = (): ReactProfilerStore | null => {
  if (!ENABLE_REACT_PROFILER || typeof window === 'undefined') return null;

  if (!window.__flashReactProfile) {
    window.__flashReactProfile = {
      samples: [],
      reset() {
        this.samples.length = 0;
      }
    };
  }

  return window.__flashReactProfile;
};

const onRender = (
  id: string,
  phase: ReactProfilerPhase,
  actualDuration: number,
  baseDuration: number,
  startTime: number,
  commitTime: number
) => {
  const store = getStore();
  if (!store) return;

  store.samples.push({
    id,
    phase,
    actualDuration,
    baseDuration,
    startTime,
    commitTime,
    at: performance.now(),
    path: window.location.pathname
  });

  if (store.samples.length > MAX_SAMPLES) {
    store.samples.splice(0, store.samples.length - MAX_SAMPLES);
  }
};

export const withReactProfiler = (id: string, children: React.ReactNode): React.ReactNode => {
  if (!ENABLE_REACT_PROFILER) return children;
  return (
    <Profiler id={id} onRender={onRender}>
      {children}
    </Profiler>
  );
};
